// =============================================================================
// MENU SECTION — Restaurant Menu Display Component
// =============================================================================
// Renders the restaurant's menu with:
// - Category tabs as anchored navigation
// - Individual menu items with name, price, description
// - Context-aware internationalization (categories, dishes, descriptions, diets, allergens)
// - Price formatting using Intl.NumberFormat
// - ZERO emojis — pure typography and clean badges
// =============================================================================

import { useTranslation } from 'react-i18next';
import type { Menu, MenuItem, MenuCategory } from '@webzite/content-schema';
import './MenuSection.css';

export interface MenuSectionProps {
  /** The complete menu data from menu.json */
  menu: Menu;
  /** Currency code for price formatting (e.g., "EUR", "USD") */
  currency: string;
  /** Locale for number formatting (e.g., "en-US", "fr-FR") */
  locale?: string;
  /** Section heading text (translated via i18n) */
  heading?: string;
  /** Allergen note / disclaimer text */
  allergenNote?: string;
}

export function MenuSection({
  menu,
  currency,
  locale = 'en-US',
  heading,
  allergenNote,
}: MenuSectionProps) {
  const { t } = useTranslation();

  // Sort categories by sortOrder
  const sortedCategories = [...menu.categories].sort((a, b) => a.sortOrder - b.sortOrder);

  // Format price with correct currency symbol and locale
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
    }).format(price);
  };

  const sectionHeading = heading ?? t('menu.heading', 'Our Menu');

  return (
    <section className="menu-section section" id="menu" aria-labelledby="menu-heading">
      <div className="container">
        {/* --- Section Heading --- */}
        <h2 id="menu-heading" className="menu-heading text-center">
          <span className="menu-heading-badge">{sectionHeading}</span>
        </h2>

        {/* --- Category Navigation (tab-style) --- */}
        <nav className="menu-category-nav" aria-label="Menu categories">
          <ul className="menu-category-tabs" role="list">
            {sortedCategories.map((category) => {
              const localizedName = t(`menu.categories.${category.id}.name`, {
                defaultValue: category.name,
              });
              return (
                <li key={category.id}>
                  <a href={`#menu-${category.id}`} className="menu-category-tab">
                    {localizedName}
                  </a>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* --- Menu Categories & Items --- */}
        {sortedCategories.map((category) => (
          <MenuCategoryBlock
            key={category.id}
            category={category}
            items={menu.items.filter((item) => item.categoryId === category.id && item.available)}
            formatPrice={formatPrice}
          />
        ))}

        {/* --- Allergen Legend --- */}
        <AllergenLegend note={allergenNote} />
      </div>
    </section>
  );
}

// --- Internal Components ---

/** Renders a single menu category with its items */
function MenuCategoryBlock({
  category,
  items,
  formatPrice,
}: {
  category: MenuCategory;
  items: MenuItem[];
  formatPrice: (price: number) => string;
}) {
  const { t } = useTranslation();
  if (items.length === 0) return null;

  const localizedName = t(`menu.categories.${category.id}.name`, {
    defaultValue: category.name,
  });
  const localizedDescription = category.description
    ? t(`menu.categories.${category.id}.description`, {
        defaultValue: category.description,
      })
    : undefined;

  return (
    <div className="menu-category" id={`menu-${category.id}`}>
      <h3 className="menu-category-name">{localizedName}</h3>
      {localizedDescription && <p className="menu-category-description">{localizedDescription}</p>}

      <div className="menu-items">
        {items.map((item) => (
          <MenuItemCard key={item.id} item={item} formatPrice={formatPrice} />
        ))}
      </div>
    </div>
  );
}

/** Renders a single menu item with localized name, description, and badges */
function MenuItemCard({
  item,
  formatPrice,
}: {
  item: MenuItem;
  formatPrice: (price: number) => string;
}) {
  const { t } = useTranslation();

  const localizedName = t(`menu.items.${item.id}.name`, {
    defaultValue: item.name,
  });
  const localizedDescription = item.description
    ? t(`menu.items.${item.id}.description`, {
        defaultValue: item.description,
      })
    : undefined;

  return (
    <article className="menu-item" id={`item-${item.id}`}>
      {item.image && (
        <div className="menu-item-image">
          <img
            src={item.image}
            alt={localizedName}
            className="img-cover"
            loading="lazy"
            width={120}
            height={120}
          />
        </div>
      )}

      <div className="menu-item-content">
        {/* Name + dots + price */}
        <div className="menu-item-header">
          <h4 className="menu-item-name">{localizedName}</h4>
          <span className="menu-item-dots" aria-hidden="true" />
          <span className="menu-item-price">{formatPrice(item.price)}</span>
        </div>

        {/* Description */}
        {localizedDescription && <p className="menu-item-description">{localizedDescription}</p>}

        {/* Diet and allergen badges (Localized, typography focused, no emojis) */}
        {(item.diets.length > 0 || item.allergens.length > 0) && (
          <div className="menu-item-tags">
            {item.diets.map((diet) => {
              const label = t(`diet.${diet}`, { defaultValue: diet });
              return (
                <span key={diet} className="badge badge-diet" title={label}>
                  {label}
                </span>
              );
            })}

            {item.allergens.map((allergen) => {
              const label = t(`allergen.${allergen}`, { defaultValue: allergen });
              return (
                <span key={allergen} className="badge badge-allergen" title={label}>
                  {label}
                </span>
              );
            })}
          </div>
        )}
      </div>
    </article>
  );
}

/** Allergen notice at the bottom of the menu */
function AllergenLegend({ note }: { note?: string }) {
  const { t } = useTranslation();

  return (
    <div className="allergen-legend" aria-label="Allergen notice">
      <p className="text-label">{t('menu.allergens.heading', 'Allergen Information')}</p>
      <p className="allergen-legend-note">
        {note ??
          t(
            'menu.allergens.note',
            'Please inform our team of any food allergies or special dietary requirements before placing your order. All dishes may contain traces of allergens due to shared kitchen environments.',
          )}
      </p>
    </div>
  );
}
